import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ExcelJS from 'exceljs';
import { CalendarIcon } from '@heroicons/react/24/outline';

const ReportsSection = () => {
  const [startDate, setStartDate] = useState(new Date('2025-08-01'));
  const [endDate, setEndDate] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleGenerateReport = async () => {
    try {
      setIsLoading(true);
      setError(null);
      console.log('Starting report generation...');
      
      const startStr = startDate.toISOString().split('T')[0];
      const endStr = endDate.toISOString().split('T')[0];
      console.log('Date range:', startStr, 'to', endStr);

      // Fetch device information for area mapping
      console.log('Fetching device data...');
      const deviceResponse = await fetch('https://njs-01.optimuslab.space/city_u/optimus/device_management');
      if (!deviceResponse.ok) throw new Error('Failed to fetch device data');
      const deviceData = await deviceResponse.json();
      console.log('Device data received:', deviceData.length, 'devices');

      // Create device mapping for Area_Name lookup with custom display names
      const deviceMap = {};
      const areaDisplayMapping = {
        'Main Entrance': '6/F Lobby',
        '2nd Entrance': '6/F Activity Rooms', 
        'Corridor': '7/F Activity Rooms'
      };
      
      deviceData.forEach(device => {
        deviceMap[device.Device_ID] = {
          area: areaDisplayMapping[device.area] || `${device.floor} ${device.area}`,
          location: device.location,
          floor: device.floor,
          building: device.building
        };
      });

      // Fetch visit data
      console.log('Fetching visit data...');
      const currentDate = new Date().toISOString().split('T')[0];
      const visitResponse = await fetch(`https://njs-01.optimuslab.space/city_u/optimus/visits?start_time=2025-08-01&end_time=${currentDate}`);
      if (!visitResponse.ok) throw new Error('Failed to fetch visit data');
      const visitData = await visitResponse.json();
      console.log('Visit data received:', visitData.length, 'records');

      // Filter records that fall exactly within start and end date
      const filteredData = visitData.filter((record) => {
        const dateStr = record.date.replace(/,.*$/, '');
        const recordDate = new Date(dateStr);
        const sDate = new Date(startDate);
        sDate.setHours(0, 0, 0, 0);
        const eDate = new Date(endDate);
        eDate.setHours(23, 59, 59, 999);
        return recordDate >= sDate && recordDate <= eDate;
      });
      console.log('Filtered data:', filteredData.length, 'records');

      // Map the data according to the required format
      const finalData = filteredData.map((entry) => ({
        Device_ID: entry.Device_ID,
        Area_Name: deviceMap[entry.Device_ID]?.area || 'Unknown Area',
        Date: entry.date,
        Visit: entry.Total_visits
      }));
      console.log('Final data prepared:', finalData.length, 'rows');

      // Generate Excel file using ExcelJS
      console.log('Creating Excel workbook...');
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('SDS Report');

      // Add headers
      worksheet.columns = [
        { header: 'Device_ID', key: 'Device_ID', width: 15 },
        { header: 'Area_Name', key: 'Area_Name', width: 25 },
        { header: 'Date', key: 'Date', width: 25 },
        { header: 'Visit', key: 'Visit', width: 10 }
      ];

      // Add rows
      finalData.forEach(row => worksheet.addRow(row));
      console.log('Excel data added');

      // Generate filename
      const filename = `SDS_CITYU-SDS_${startStr}_${endStr}.xlsx`;
      console.log('Generating file:', filename);

      // Save file
      const buffer = await workbook.xlsx.writeBuffer();
      console.log('Buffer created, size:', buffer.byteLength);
      
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('Download triggered successfully');

    } catch (err) {
      console.error('Error generating report:', err);
      setError(err.message);
    } finally {
      console.log('Setting loading to false');
      setIsLoading(false);
    }
  };

  const CustomInput = React.forwardRef(({ value, onClick, placeholder }, ref) => (
    <div className="relative">
      <input
        type="text"
        value={value}
        onClick={onClick}
        ref={ref}
        placeholder={placeholder}
        readOnly
        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
      />
      <CalendarIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
    </div>
  ));

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-semibold text-gray-900 mb-2">Reports</h2>
      <p className="text-gray-600 mb-8 text-left">Please select a date range</p>

      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
        <div className="flex flex-col sm:flex-row gap-4 flex-1">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start date
            </label>
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              customInput={<CustomInput placeholder="Start date" />}
              dateFormat="MMM dd, yyyy"
              maxDate={new Date()}
            />
          </div>

          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End date
            </label>
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              customInput={<CustomInput placeholder="End date" />}
              dateFormat="MMM dd, yyyy"
              maxDate={new Date()}
            />
          </div>
        </div>

        <div className="flex-shrink-0">
          <button
            onClick={handleGenerateReport}
            disabled={isLoading}
            className={`px-8 py-3 rounded-lg font-medium transition-colors duration-200 shadow-sm hover:shadow-md ${
              isLoading 
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            {isLoading ? (
              <div className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Generating...
              </div>
            ) : (
              'Generate Report'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportsSection;